/* eslint-disable react/jsx-key */

import getKey from 'lodash/get';

import React, { PureComponent } from 'react';
import path from 'path';
import styled, { css, createGlobalStyle } from 'styled-components';

import {
  UserOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';

import Admin from 'hive-admin';
import PageSingle from 'hive-admin/src/components/PageSingle';

import Popover from 'antd/lib/popover';
import Button from 'antd/lib/button';
import Descriptions from 'antd/lib/descriptions';
import List from 'antd/lib/list';
import Avatar from 'antd/lib/avatar';

import { renderCurrency } from '../components/SystemCountry';

import Types from '../modules/types';

const {
  CONSTANTS: {
    OrderStatus,
  },
} = Types;

const useTokenFromPathOrRedirectToLogin = ({
  isInitializing,
  isAuthorized,
  location,
  storage,
  match,
}) => {
  if (match && match.params && match.params.token) {
    storage.setItem('ACCESS_TOKEN', match.params.token);
    window.location.pathname = path.resolve(location.pathname, '../');
    return false;
  }
  if (!isInitializing && !isAuthorized) {
    return '/login';
  }
  return false;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 50px auto;
  width: 100%;
  max-width: 300px;
  padding: 40px;
  border-radius: 10px;
  background-color: #fefefe;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
  text-align: left;
  .ant-descriptions-item {
    display: flex;
    .ant-descriptions-item-label {
      vertical-align: top;
    }
  }
`;

const Spacer = styled.div`
  height: ${({ height = 0 }) => height}px;
`;

function TitleHTML({ alone, ...props }) {
  // eslint-disable-next-line jsx-a11y/heading-has-content
  return <h1 {...props} />;
}

const Title = styled(TitleHTML)`
  font-size: 40px;
  line-height: 1.2;
  font-weight: 700;
  ${({ alone }) => alone && css`
    margin-bottom: 0px;
  `}
`;

const ActionPopConfirmTitle = styled.div`
  font-size: 20px;
  padding: 10px 5px;
`;

const ActionPopConfirmWrapper = styled.div`
  display: flex;
  width: calc(100vw - 140px);
  max-width: 240px;
  .ant-btn {
    width: 100%;
    &[data-cancel="true"] {
      margin-left: -5px;
      margin-right: 5px;
    }
    &[data-ok="true"] {
      margin-left: 5px;
      margin-right: -5px;
    }
  }
`;

const ActionPopConfirmGlobalStyle = createGlobalStyle`
  body > .action-pop-confirm-container {
    position: fixed;
    top: 0px;
    left: 0px;
    bottom: 0px;
    right: 0px;
    pointer-events: none;
    background-color: transparent;
    transition: background-color 300ms;
    &[data-visible="true"] {
      pointer-events: all;
      background-color: rgba(255, 255, 255, 0.8);
    }
  }
`;

class ActionPopConfirm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { visible: false };
  }

  handleVisibleChange = (visible) => {
    this.setState({ visible: !!visible });
    this.props.container.setAttribute('data-visible', !!visible);
  }

  handleOkClick = (ev) => {
    this.setState({ visible: false });
    this.props.container.setAttribute('data-visible', false);
    this.props.onClick(ev);
  }

  handleCancelClick = () => {
    this.setState({ visible: false });
    this.props.container.setAttribute('data-visible', false);
  }

  handleGetPopupContainer = () => this.props.container

  renderContent() {
    return (
      <ActionPopConfirmWrapper>
        <Button
          type="danger"
          size="large"
          data-cancel="true"
          onClick={this.handleCancelClick}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          block
          onClick={this.handleOkClick}
          data-ok="true"
          {...this.props.params}
        >
          OK
        </Button>
      </ActionPopConfirmWrapper>
    );
  }

  render() {
    return (
      <Popover
        trigger="click"
        title={(
          <ActionPopConfirmTitle>
            {this.props.title}
          </ActionPopConfirmTitle>
        )}
        content={this.renderContent()}
        visible={this.state.visible}
        getPopupContainer={this.handleGetPopupContainer}
        onVisibleChange={this.handleVisibleChange}
      >
        {this.props.children}
      </Popover>
    );
  }
}

export class PageApprove extends PageSingle {
  static config = {
    ...PageSingle.config,
    ClassName: 'PageApprove',
  }

  constructor(props) {
    super(props);
    this.popoverContainer = window.document.createElement('div');
    this.popoverContainer.setAttribute('class', 'action-pop-confirm-container');
    this.popoverContainer.setAttribute('data-visible', false);
  }

  componentDidMount() {
    super.componentDidMount();
    window.document.body.append(this.popoverContainer);
  }

  componentWillUnmount() {
    window.document.body.removeChild(this.popoverContainer);
    super.componentWillUnmount();
  }

  handleActionClick = (event) => {
    const {
      action,
      success: successString,
      result,
    } = getKey(event, 'target.dataset', {});
    // this.setState({ loading: true });
    const success = successString === 'true';
    const hideWorking = Admin.showMessage('Processing', 'loading', 0);
    this.props.client.request({
      url: `orders/${this.props.data._id}/${action}`,
      method: 'POST',
      data: { success },
    })
    .then(() => {
      hideWorking();
      Admin.showMessage('Done!', 'success', 3);
      if (result && result.length) {
        this.setState({ result });
      } else {
        this.props.reload();
      }
    })
    .catch((error) => {
      hideWorking();
      Admin.showMessage(error.message, 'error', 3);
    });
  }

  renderOrderHeader(order) {
    return (
      <Descriptions title="Order" column={1}>
        <Descriptions.Item label="ID">
          {order._id}
        </Descriptions.Item>
        {
          order.shipping
          ? (
              <Descriptions.Item label="Shipping">
                {Types.SHIPPING_METHOD_LABELS_MAP[order.shipping.method]}
              </Descriptions.Item>
            )
          : null
        }
        {
          order.payment
          ? (
              <Descriptions.Item label="Payment">
                {Types.PAYMENT_METHOD_LABELS[
                  Types.PAYMENT_METHOD.indexOf(order.payment.method)
                ]}
              </Descriptions.Item>
            )
          : null
        }
      </Descriptions>
    );
  }

  renderOrderDestination(title, details) {
    return (
      <Descriptions title={title} column={1}>
        <Descriptions.Item label={<UserOutlined />}>
          {details.name}
        </Descriptions.Item>
        <Descriptions.Item label={<MailOutlined />}>
          <a href={`mailto:${details.email}`}>
            {details.email}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label={<PhoneOutlined />}>
          <a href={`tel:${details.phoneNumberString}`}>
            {details.phoneNumberString}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label={<EnvironmentOutlined />}>
          {details.address.lines}
        </Descriptions.Item>
      </Descriptions>
    );
  }

  renderOrderCost(order) {
    return (
      <Descriptions title="Cost" column={1}>
        {
          [
            ['Items', renderCurrency({ suffix: ` ${
              Types.decimalize(order.cost.items || 0)
            }` })],
            (
              order.cost.itemsDiscount > 0
              ? ['Items Discount', renderCurrency({ suffix: ` ${
                  Types.decimalize(order.cost.itemsDiscount)
                }` })]
              : null
            ),
            ['Shipping', renderCurrency({ suffix: ` ${
              Types.decimalize(order.cost.shipping || 0)
            }${
              order.shipping.free === true
              ? ' (free)'
              : ''
            }` })],
            (
              order.cost.vat
              ? ['VAT', renderCurrency({ suffix: ` ${
                  Types.decimalize(order.cost.vat || 0)
                }` })]
              : null
            ),
            (
              order.cost.payment > 0
              ? [`Payment (${
                  Types.PAYMENT_METHOD_LABELS_MAP[order.payment.method]
                })`, renderCurrency({ suffix: ` ${
                  Types.decimalize(order.cost.payment || 0)
                }` })]
              : null
            ),
            ['Charged', renderCurrency({ suffix: ` ${
              Types.decimalize(order.cost.charged || 0)
            }` })],
          ]
          .reduce(
            (items, item) => {
              if (item) {
                items.push(
                  <Descriptions.Item key={item[0]} label={item[0]}>
                    {item[1]}
                  </Descriptions.Item>
                );
              }
              return items;
            },
            [],
          )
        }
      </Descriptions>
    );
  }

  renderOrderItems(items) {
    return (
      <Descriptions title="Items" column={1}>
        <Descriptions.Item>
          <List
            itemLayout="horizontal"
            dataSource={items}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta
                  avatar={(
                    item.image && item.image.src
                    ? <Avatar src={item.image.src} />
                    : null
                  )}
                  title={`${item.quantity} x ${item.name}`}
                />
              </List.Item>
            )}
          />
        </Descriptions.Item>
      </Descriptions>
    );
  }

  renderOrderAction(title, action, success, popconfirm = true, result = '') {
    const buttonProps = {
      type: success ? 'primary' : 'danger',
      size: 'large',
      style: { width: '100%', marginBottom: '10px' },
    };
    const actionProps = {
      'data-action': action,
      'data-success': !!success,
      'data-result': result,
    };
    return popconfirm
    ? (
        <ActionPopConfirm
          title={`${title}?`}
          onClick={this.handleActionClick}
          container={this.popoverContainer}
          params={actionProps}
        >
          <Button
            {...buttonProps}
          >
            {title}
          </Button>
        </ActionPopConfirm>
      )
    : (
        <Button
          {...buttonProps}
          {...actionProps}
          onClick={this.handleActionClick}
        >
          {title}
        </Button>
      );
  }

  renderFound() {
    const {
      status,
      shippingDetails,
      items,
    } = this.props.data;
    const { result } = this.state || {};
    let content = null;
    if (result) {
      content = <Title alone>{result}</Title>;
    } else if (status === OrderStatus.APPROVAL) {
      content = (
        <>
          <Title>New Order</Title>
          <Spacer height={20} />
          {this.renderOrderHeader(this.props.data)}
          <Spacer height={20} />
          {this.renderOrderDestination('Customer', shippingDetails)}
          <Spacer height={20} />
          {this.renderOrderCost(this.props.data)}
          <Spacer height={20} />
          {this.renderOrderItems(items)}
          <Spacer height={20} />
          {this.renderOrderAction(
            'Approve',
            'approval',
            true,
            true, // change to false to disable popconfirm
            'Order Approved',
          )}
          {this.renderOrderAction(
            'Deny',
            'approval',
            false,
            true, // change to false to disable popconfirm
            'Order Denied',
          )}
        </>
      );
    }
    if (!content) {
      content = (
        <>
          <ActionPopConfirmGlobalStyle />
          <Title alone>
            Order cannot be approved at this moment
          </Title>
        </>
      );
    }
    return (
      <>
        <ActionPopConfirmGlobalStyle />
        <Wrapper>{content}</Wrapper>
      </>
    );
  }
}

export default [config => PageApprove.create(config), {
  title: 'Approval',
  icon: null,
  path: '/approve/:id/:token?',
  hideSidebar: true,
  hideHeader: true,
  hidden: [() => true],
  redirect: [useTokenFromPathOrRedirectToLogin],
  activity: 'edit',
  editConfig: { actions: [] },
  useTrash: false,
  headerTitle: 'Approval',
  loadUrl: '/orders/:id',
  saveMethod: 'post',
  loadExtractData: response => (response ? response.data : null),
  notFoundMessage: 'Order not found!',
}];
